import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Avatar, Card, EmptyState, Skeleton, Tabs, Chip, fmtNum } from "../ui";
import { useAsync } from "../../hooks";
import content from "../../services/api-content";
import "../../styles/content.css";

const METRIC = [{ value: "chats", label: "แชทมากสุด" }, { value: "likes", label: "ถูกใจมากสุด" }, { value: "gifts", label: "ของขวัญมากสุด" }];
const UNIT = { chats: "แชท", likes: "ไลก์", gifts: "ของขวัญ" };
const PERIOD = [{ value: "day", label: "วันนี้" }, { value: "week", label: "7 วัน" }];

function Rows({ rows, kind, unit }) {
  if (!rows.length) return <EmptyState icon="🏆" title="ยังไม่มีข้อมูลในช่วงนี้">เมื่อมีกิจกรรม อันดับจะแสดงที่นี่</EmptyState>;
  const to = (r) => (kind === "characters" ? `/c/${r.id}` : kind === "universes" ? `/universe/${r.id}` : `/creator/${r.id}`);
  const nm = (r) => r.name || r.displayName || "";
  const top = rows.slice(0, 3);
  return (
    <>
      <div className="podium">
        {top.map((r) => (
          <Link key={r.id} to={to(r)} className="podium__item" aria-label={`อันดับ ${r.rank} ${nm(r)}`}>
            <span className={`rank-n top`}>{["🥇", "🥈", "🥉"][r.rank - 1] || r.rank}</span>
            <Avatar name={nm(r)} seed={r.id} colors={r.colors} src={r.image || r.photoURL} size={r.rank === 1 ? 64 : 52} square={kind !== "creators"} character={kind !== "creators"} />
            <div className="strong clamp-1" style={{ maxWidth: "100%" }}>{nm(r)}</div>
            <div className="muted" style={{ fontSize: 12 }}>{fmtNum(r.value)} {unit}</div>
          </Link>
        ))}
      </div>
      <div className="stack">
        {rows.slice(3).map((r) => (
          <Link key={r.id} to={to(r)}>
            <Card link className="row">
              <span className="rank-n">{r.rank}</span>
              <Avatar name={nm(r)} seed={r.id} colors={r.colors} src={r.image || r.photoURL} size={44} square={kind !== "creators"} character={kind !== "creators"} />
              <div className="grow"><div className="strong clamp-1">{nm(r)}</div>{r.creatorName ? <div className="faint clamp-1" style={{ fontSize: 12 }}>โดย {r.creatorName}</div> : null}</div>
              <div className="muted" style={{ fontSize: 13 }}>{fmtNum(r.value)} {unit}</div>
            </Card>
          </Link>
        ))}
      </div>
    </>
  );
}

/** อันดับ ตัวละคร/จักรวาล/ครีเอเตอร์ — ใช้ทั้งหน้า /ranking และแท็บในหน้าแรก */
export default function RankingView() {
  const [kind, setKind] = useState("characters");
  const [metric, setMetric] = useState("chats");
  const [period, setPeriod] = useState("week");
  const rank = useAsync(() => content.rankings(period), [period]);
  const uni = useAsync(() => (kind === "universes" ? content.universes.list({ sort: "popular", limit: 10 }) : Promise.resolve(null)), [kind]);

  let body;
  if (kind === "universes") {
    const rows = (uni.data?.items || []).map((u, i) => ({ rank: i + 1, id: u.id, name: u.name, image: u.image, emoji: u.emoji, colors: u.colors, value: u.messageCount || 0 }));
    body = uni.error ? <div className="error-text">{uni.error}</div> : uni.loading ? <Skeleton height={220} /> : <Rows rows={rows} kind="universes" unit="ข้อความ" />;
  } else {
    body = rank.error ? <div className="error-text">{rank.error}</div> : rank.loading ? <Skeleton height={220} /> : <Rows rows={rank.data[kind][metric]} kind={kind} unit={UNIT[metric]} />;
  }
  return (
    <div>
      <Tabs tabs={[{ value: "characters", label: "ตัวละคร" }, { value: "universes", label: "จักรวาล" }, { value: "creators", label: "ครีเอเตอร์" }]} value={kind} onChange={setKind} />
      {kind !== "universes" ? (
        <div className="row wrap mt-16">
          <div className="chip-row">{METRIC.map((m) => <Chip key={m.value} active={metric === m.value} onClick={() => setMetric(m.value)}>{m.label}</Chip>)}</div>
          <div className="grow" />
          <div className="chip-row">{PERIOD.map((m) => <Chip key={m.value} size="sm" active={period === m.value} onClick={() => setPeriod(m.value)}>{m.label}</Chip>)}</div>
        </div>
      ) : null}
      <div className="mt-16">{body}</div>
      {kind !== "universes" && rank.data?.since ? <p className="faint mt-16" style={{ fontSize: 12 }}>นับเฉพาะกิจกรรมตั้งแต่ {new Date(rank.data.since).toLocaleDateString("th-TH", { dateStyle: "medium" })} · อัปเดตทุกไม่กี่นาที</p> : null}
    </div>
  );
}
